import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserCheck, AlertCircle, Loader, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { acceptInvitationWithAuth } from '../services/supabase/tenantInvitations';
import Layout from '../components/layout/Layout';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error, needsAuth
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Brak tokenu zaproszenia w adresie URL');
      return;
    }

    if (!user) {
      // User not logged in - redirect to login with return URL
      setStatus('needsAuth');
      return;
    }

    // User is logged in, accept invitation
    async function acceptInvite() {
      try {
        setStatus('loading');
        const result = await acceptInvitationWithAuth(token);
        setInvitation(result);
        setStatus('success');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } catch (err) {
        console.error('Error accepting invitation:', err);
        setStatus('error');
        setError(err.message);
      }
    }

    acceptInvite();
  }, [token, user, navigate]);

  const handleLoginRedirect = () => {
    navigate(`/login?redirect=/accept-invitation?token=${token}`);
  };

  const handleRegisterRedirect = () => {
    navigate(`/register?redirect=/accept-invitation?token=${token}`);
  };

  return (
    <Layout showHeader={false}>
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Przetwarzanie zaproszenia...
              </h2>
              <p className="text-slate-600">
                Proszę czekać, akceptujemy Twoje zaproszenie.
              </p>
            </div>
          )}

          {/* Needs Authentication State */}
          {status === 'needsAuth' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCheck className="w-8 h-8 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Wymagane logowanie
              </h2>
              <p className="text-slate-600 mb-6">
                Aby zaakceptować zaproszenie do nieruchomości, musisz się zalogować lub utworzyć konto.
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleLoginRedirect}
                  className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Zaloguj się
                </button>
                <button
                  onClick={handleRegisterRedirect}
                  className="w-full py-3 px-4 bg-white text-violet-600 border-2 border-violet-600 rounded-xl font-semibold hover:bg-violet-50 transition-all"
                >
                  Utwórz konto
                </button>
              </div>

              <p className="text-sm text-slate-500 mt-4">
                Po zalogowaniu zaproszenie zostanie automatycznie zaakceptowane.
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Zaproszenie zaakceptowane!
              </h2>
              <p className="text-slate-600 mb-4">
                Masz teraz dostęp do nieruchomości jako najemca.
              </p>

              {invitation?.properties && (
                <div className="bg-violet-50 rounded-xl p-4 mb-4 text-left">
                  <p className="text-sm font-semibold text-violet-900 mb-1">
                    Nieruchomość:
                  </p>
                  <p className="text-sm text-violet-800">
                    {`${invitation.properties.street} ${invitation.properties.building_number}${invitation.properties.apartment_number ? `/${invitation.properties.apartment_number}` : ''}, ${invitation.properties.city}`}
                  </p>
                  {invitation.permissions?.upload_photos && (
                    <p className="text-xs text-violet-700 mt-2">
                      ✅ Możesz dodawać inspekcje okresowe
                    </p>
                  )}
                </div>
              )}

              <p className="text-sm text-slate-500">
                Przekierowanie do panelu nieruchomości...
              </p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Błąd akceptacji zaproszenia
              </h2>
              <p className="text-slate-600 mb-6">
                {error || 'Wystąpił nieoczekiwany błąd'}
              </p>

              <button
                onClick={() => navigate('/properties')}
                className="w-full py-3 px-4 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-700 transition-all"
              >
                Wróć do panelu
              </button>

              <div className="mt-6 p-4 bg-amber-50 rounded-xl text-left">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  Możliwe przyczyny:
                </p>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Zaproszenie wygasło (ważne 7 dni)</li>
                  <li>• Zaproszenie zostało anulowane</li>
                  <li>• Zaproszenie jest na inny adres email</li>
                  <li>• Link został uszkodzony</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
